import RotateLeftIcon from '@mui/icons-material/RotateLeft';
import {
    Box,
    Card,
    CardContent,
    Grid,
    MenuItem,
    Select,
    Stack,
    Tooltip,
    Typography
} from "@mui/material";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TableSortLabel from "@mui/material/TableSortLabel";
import { visuallyHidden } from "@mui/utils";
import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import useDebounce from "../../hooks/use.debounce";
import { getFormatedDate } from "../../utils/common";
import { CustomSearchInput, FORM_SELECTFIELD_STYLES, START_ADORNMENT_FOR_CUSTOM_SEARCH_INPUT, TABLE_BODY_ROW_MUI_STYLES, TABLE_HEADER_ROW_MUI_STYLES } from "../../utils/constant";
import CustomPagination from "../../utils/CustomPagination";
import { ROUTES } from "../../utils/routes";
import { getCampaignsByInfluencer, getCampaignStats } from './campaign_by_influencer/action';
import { updateDetail } from './campaign_by_influencer/slice';

// Reuse EnhancedTableHead from existing code or create new
function EnhancedTableHead(props) {
    const { order, orderBy, onRequestSort, isSticky } = props;
    const createSortHandler = (property) => (event) => {
        if (property === "action") {
            return;
        }
        onRequestSort(event, property);
    };

    const headCells = [
        { id: "sno", numeric: false, disablePadding: true, label: "SI No.", type: "" },
        { id: "companyName", numeric: false, disablePadding: true, label: "Company Name", type: "" },
        { id: "brandName", numeric: false, disablePadding: true, label: "Brand Name", type: "" },
        { id: "influencerName", numeric: false, disablePadding: true, label: "Influencer Name", type: "" },
        { id: "campaignName", numeric: false, disablePadding: true, label: "Campaign Name", type: "" },
        { id: "totalAmount", numeric: false, disablePadding: true, label: "Amount", type: "" },
        { id: "status", numeric: false, disablePadding: true, label: "Status", type: "" },
        { id: "type", numeric: false, disablePadding: true, label: "Type", type: "" }, // paid, barter etc
        { id: "lastDateOfPayment", numeric: false, disablePadding: true, label: "End Date", type: "" },
        { id: "action", numeric: false, disablePadding: true, label: "Actions", type: "" },
    ];

    return (
        <TableHead className={`hideActionSort ${isSticky} `}>
            <TableRow sx={TABLE_HEADER_ROW_MUI_STYLES}>
                {headCells.map((headCell) => (
                    <TableCell
                        key={headCell?.id}
                        align={headCell.numeric ? "right" : "left"}
                        padding={headCell.disablePadding ? "none" : "normal"}
                        sortDirection={orderBy === headCell.id ? order : false}
                        sx={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
                    >
                        <TableSortLabel
                            active={orderBy === headCell.id}
                            direction={orderBy === headCell.id ? order : "asc"}
                            IconComponent={null}
                            onClick={createSortHandler(headCell.id)}
                        >
                            {headCell.label}
                            {orderBy === headCell.id ? (
                                <Box component="span" sx={visuallyHidden}>
                                    {order === "desc" ? "sorted descending" : "sorted ascending"}
                                </Box>
                            ) : null}
                        </TableSortLabel>
                    </TableCell>
                ))}
            </TableRow>
        </TableHead>
    );
}

const StatCard = ({ title, count, color }) => (
    <Card sx={{ minWidth: 200, flex: 1, boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.05)', borderRadius: '12px' }}>
        <CardContent>
            <Typography sx={{ fontSize: 14, color: '#666', mb: 1.5 }} gutterBottom>
                {title}
            </Typography>
            <Typography variant="h3" component="div" sx={{ fontWeight: 'bold' }}>
                {count}
            </Typography>
        </CardContent>
    </Card>
);

export default function CampaignByInfluencerList(props) {
    const navigate = useNavigate();

    const states = useSelector((state) => state.campaignByInfluencerSlice);
    const {
        limit,
        page,
        sort_key,
        totalPages,
        filters,
        list,
        totalCount,
        stats
    } = states || { limit: 10, page: 0, sort_key: 'id', totalPages: 0, filters: {}, list: [], totalCount: 0, stats: { total: 0, active: 0, completed: 0, expired: 0 } };

    console.log(limit, page, sort_key, totalPages, filters, list, totalCount);
    const userType = localStorage.getItem('type');

    const [order, setOrder] = useState("desc");
    const [orderBy, setOrderBy] = useState("id");
    const [sticky] = useState("");
    const dispatch = useDispatch();

    const debouncedSearch = useDebounce(filters.name, 500);

    const CAMPAIGN_TYPES = ['paid', 'barter', 'affiliate', 'performance'];

    const handleRequestSort = (event, property) => {
        const isAsc = orderBy === property && order === "asc";
        setOrder(isAsc ? "desc" : "asc");
        setOrderBy(property);
        dispatch(updateDetail({ sort_type: isAsc ? "asc" : "desc", sort_key: property }));
    };

    useEffect(() => {
        if (debouncedSearch || filters.name === "") {
            dispatch(getCampaignsByInfluencer());
            // Stats should also refresh on search? Maybe not, usually stats are overall. 
            // But if stats should reflect filters, then yes. 
            // Current stats API implementation respects companyId/influencerId but not name search.
            // So calling it once or appropriately is fine.
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [debouncedSearch]);

    useEffect(() => {
        dispatch(getCampaignsByInfluencer());
        dispatch(getCampaignStats());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleChange = (value) => {
        dispatch(updateDetail({ page: value - 1 }));
        dispatch(getCampaignsByInfluencer());
    };

    const handleSearch = (event) => {
        dispatch(updateDetail({ filters: { ...filters, name: event.target.value } }));
    };

    const handleSearchClear = () => {
        dispatch(updateDetail({ filters: { ...filters, name: "" } }));
        dispatch(getCampaignsByInfluencer());
    };

    const handleChangePerPage = (event, value) => {
        dispatch(updateDetail({ limit: event.target.value, page: 0 }));
        dispatch(getCampaignsByInfluencer());
    };

    const tableRef = useRef();

    const getStatusStyle = (status) => {
        const s = status?.toLowerCase();
        if (s === 'completed') return { background: '#DEFADE', color: '#1B5E20' }; // Green
        if (s === 'expired') return { background: '#FFEBEE', color: '#C62828' }; // Red
        if (s === 'active' || s === 'pending') return { background: '#E3F2FD', color: '#0D47A1' }; // Blue
        return { background: '#F5F5F5', color: '#616161' };
    };

    return (
        <>
            <Box mb={4} px={4}>
                <Typography className="page-title" variant="h1" gutterBottom sx={{ mb: 3 }}>All Pay List</Typography>
                <Grid container spacing={3}>
                    <Grid item xs={12} md={4}>
                        <StatCard title="All Links" count={stats?.total || 0} />
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <StatCard title="Completed Payment" count={stats?.completed || 0} />
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <StatCard title="Pending Payment" count={stats?.active || 0} />
                    </Grid>
                    {/* <Grid item xs={6} md={3}>
                        <StatCard title="Expired Payments" count={stats?.expired || 0} />
                    </Grid> */}
                </Grid>
            </Box>

            <Stack direction="row" alignItems="center" mb={4}>
                <Stack
                    direction={"row"}
                    alignItems={"center"}
                    justifyContent={"space-between"}
                    flexWrap="wrap" gap={2}
                    sx={{ minHeight: '42px', paddingX: '16px', width: '100%' }}
                >
                    {/* <Typography className="page-title" variant="h1">Influencer Campaigns</Typography> */}
                    <Stack direction="row" justifyContent="flex-end" alignItems="center" flexWrap="wrap" gap={2} sx={{ width: '100%' }}>
                        <CustomSearchInput
                            className="inputSearch sContrl p-0"
                            value={filters.name}
                            placeholder={"Search Campaign or Influencer..."}
                            handleSearch={handleSearch}
                            clearSearch={handleSearchClear}
                            startAdornment={START_ADORNMENT_FOR_CUSTOM_SEARCH_INPUT}
                        />

                        <Select
                            sx={{ ...FORM_SELECTFIELD_STYLES, maxHeight: '48px', maxWidth: '200px', width: '200px' }}
                            MenuProps={{ PaperProps: { sx: { borderRadius: "8px", mt: 1, }, }, }}
                            value={filters?.type || ''}
                            name="type"
                            onChange={(e) => {
                                dispatch(updateDetail({ filters: { ...filters, type: e.target.value } }));
                                dispatch(getCampaignsByInfluencer());
                            }}
                            displayEmpty
                            defaultValue={""}
                            justifyContent={"flex-end"}
                            className="upperCase"
                        >
                            <MenuItem disabled value="" hidden>Type</MenuItem>
                            <MenuItem value="" >All</MenuItem>
                            {CAMPAIGN_TYPES.map((v) => <MenuItem className="upperCase" key={v} value={v} >{v}</MenuItem>)}
                        </Select>

                        <Tooltip title="Refresh listing">
                            <RotateLeftIcon toolTip="Refresh page" size="small" sx={{ cursor: "pointer", color: '#6C7692', mt: 1.5 }}
                                onClick={() => {
                                    dispatch(updateDetail({ filters: {} }))
                                    dispatch(getCampaignsByInfluencer());
                                    dispatch(getCampaignStats());
                                }} />
                        </Tooltip>

                    </Stack>
                </Stack>
            </Stack>

            <Stack direction={"column"} gap={2}>
                <Box sx={{
                    backgroundColor: '#fff',
                    transition: 'all .5s ease-in-out',
                    position: 'relative',
                    border: '1px solid rgba(170, 170, 170, 0.3)',
                }}>
                    <TableContainer ref={tableRef}>
                        <Table aria-label="sticky table">
                            <EnhancedTableHead
                                isSticky={sticky}
                                order={order}
                                orderBy={sort_key}
                                onRequestSort={handleRequestSort}
                            />
                            <TableBody>
                                {list && list?.length ? (
                                    list?.map((row, index) => {
                                        return (
                                            <TableRow hover role="checkbox" tabIndex={-1} key={row?.id} sx={TABLE_BODY_ROW_MUI_STYLES}>
                                                <TableCell component="td" scope="row">{(page * limit) + index + 1}</TableCell>
                                                <TableCell component="td" scope="row">{row?.companyName || "N/A"}</TableCell>
                                                <TableCell component="td" scope="row">{row?.brandName || "N/A"}</TableCell>
                                                <TableCell component="td" scope="row">
                                                    <Stack direction="row" alignItems="center" gap={1}>
                                                        {row.influencerPicture && <img src={row.influencerPicture} alt="" style={{ width: 30, height: 30, borderRadius: '50%' }} />}
                                                        {row?.influencerName || "N/A"}
                                                    </Stack>
                                                </TableCell>
                                                <TableCell component="td" scope="row">{row?.campaignName || "N/A"}</TableCell>
                                                <TableCell component="td" scope="row">{row?.totalAmount || "0"}</TableCell>
                                                <TableCell component="td" scope="row">
                                                    <span style={{
                                                        ...getStatusStyle(row?.status || 'Active'),
                                                        padding: '6px 16px',
                                                        borderRadius: '4px',
                                                        fontSize: '12px',
                                                        fontWeight: 'bold',
                                                        display: 'inline-block'
                                                    }}>
                                                        {row?.status || 'Active'}
                                                    </span>
                                                </TableCell>
                                                <TableCell component="td" scope="row" className="upperCase">{row?.type || "N/A"}</TableCell>
                                                <TableCell component="td" scope="row">{row?.lastDateOfPayment ? getFormatedDate(row?.lastDateOfPayment, "DD MMMM YYYY") : "N/A"}</TableCell>
                                                <TableCell sx={{ cursor: "pointer" }}>
                                                    <Stack direction={"row"} sx={{ gap: '8px' }}>
                                                        <div className="actBtnGrps">
                                                            <span className="viewIcon" onClick={() => {
                                                                console.log(userType);

                                                                if (userType === 'admin') {
                                                                    navigate(`${ROUTES.CAMPAIGN_BY_INFLUENCER_DETAILS_ADMIN}/${row.id}`)
                                                                } else {
                                                                    navigate(`${ROUTES.CAMPAIGN_BY_INFLUENCER_DETAILS}/${row.id}`)
                                                                }
                                                            }}>View</span>
                                                        </div>
                                                    </Stack>
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })
                                ) : (
                                    <TableRow colSpan={10}>
                                        <TableCell colSpan={10} style={{ textAlign: 'center' }}>
                                            <Typography color="inherit" variant="subtitle1" component="h3">No Data found!</Typography>
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <Stack sx={{ background: 'transparent', height: '60px', border: '1px solid #E9EAEB' }} direction="row" alignItems={"center"} justifyContent={"flex-end"}>
                        <CustomPagination
                            count={totalPages || 0}
                            onChange={handleChange}
                            currentPage={page}
                            handleChangePerPage={handleChangePerPage}
                            limit={limit}
                            totalCount={totalCount}
                            listingCount={list?.length}
                            defaultPage={1} siblingCount={1} boundaryCount={1} />
                    </Stack>
                </Box>
            </Stack>
        </>
    );
}
